import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, ChevronDown, ChevronRight, CheckCircle, XCircle, Clock, RefreshCw, Mail, Users, TrendingUp, StopCircle, Play } from "lucide-react";
import { format, differenceInMinutes } from "date-fns";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";

interface WelcomeEmailJob {
  id: string;
  status: string;
  total_count: number;
  processed_count: number;
  successful_count: number;
  failed_count: number;
  email_subject: string;
  email_body: string;
  started_by: string | null;
  created_at: string;
  completed_at: string | null;
  error_details: any;
  admin_name?: string;
}

// Check if a job is stuck (processing for more than 5 minutes without completion)
function isStuckJob(job: WelcomeEmailJob): boolean {
  if (job.status !== 'processing') return false;
  if (job.completed_at) return false;
  
  const createdAt = new Date(job.created_at);
  const minutesElapsed = differenceInMinutes(new Date(), createdAt);
  
  // Consider stuck if processing for more than 5 minutes and not complete
  return minutesElapsed > 5 && job.processed_count < job.total_count;
}

export function WelcomeEmailJobsHistory() {
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const { data: jobs, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["welcome_email_jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("welcome_email_jobs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      // Fetch admin names for started_by
      const adminIds = [...new Set(data?.map((j) => j.started_by).filter(Boolean))] as string[];
      let profileMap: Record<string, string> = {};

      if (adminIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", adminIds);

        if (profiles) {
          profileMap = Object.fromEntries(profiles.map((p) => [p.id, p.full_name || "Unknown"]));
        }
      }

      return data?.map((job) => ({
        ...job,
        admin_name: job.started_by ? profileMap[job.started_by] || "Unknown" : "System",
      })) as WelcomeEmailJob[];
    },
  });

  // Calculate summary stats
  const stats = jobs
    ? {
        totalJobs: jobs.length,
        totalEmailsSent: jobs.reduce((sum, j) => sum + (j.successful_count || 0), 0),
        successRate:
          jobs.length > 0
            ? Math.round(
                (jobs.reduce((sum, j) => sum + (j.successful_count || 0), 0) /
                  Math.max(1, jobs.reduce((sum, j) => sum + (j.total_count || 0), 0))) *
                  100
              )
            : 0,
        inProgress: jobs.filter((j) => j.status === "processing" || j.status === "pending").length,
      }
    : null;

  const handleAbort = async (jobId: string) => {
    setActionInProgress(jobId);
    try {
      const { data, error } = await supabase.functions.invoke('send-imported-welcome', {
        body: { action: 'abort', jobId }
      });

      if (error) throw error;

      toast({
        title: "Job Aborted",
        description: `Job was stopped. ${data.processed} of ${data.total} emails were sent.`,
      });
      
      refetch();
    } catch (error) {
      console.error('Failed to abort job:', error);
      toast({
        title: "Failed to Abort",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleResume = async (jobId: string, processedCount: number, totalCount: number) => {
    setActionInProgress(jobId);
    try {
      const { data, error } = await supabase.functions.invoke('send-imported-welcome', {
        body: { action: 'resume', jobId }
      });

      if (error) throw error;

      toast({
        title: "Job Resumed",
        description: `Resuming from email ${processedCount + 1} of ${totalCount}`,
      });
      
      refetch();
    } catch (error) {
      console.error('Failed to resume job:', error);
      toast({
        title: "Failed to Resume",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const getStatusBadge = (job: WelcomeEmailJob) => {
    const stuck = isStuckJob(job);
    
    switch (job.status) {
      case "completed":
        return (
          <Badge className="bg-success text-success-foreground">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case "processing":
        if (stuck) {
          return (
            <Badge variant="secondary" className="bg-warning/20 text-warning">
              <Clock className="h-3 w-3 mr-1" />
              Stuck
            </Badge>
          );
        }
        return (
          <Badge variant="secondary" className="bg-info/20 text-info">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Processing
          </Badge>
        );
      case "aborted":
        return (
          <Badge variant="secondary" className="bg-warning/20 text-warning">
            <StopCircle className="h-3 w-3 mr-1" />
            Aborted
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="outline">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{job.status}</Badge>;
    }
  };

  const getProgressDisplay = (job: WelcomeEmailJob) => {
    const { processed_count, total_count, successful_count, failed_count } = job;
    const percentage = total_count > 0 ? Math.round((processed_count / total_count) * 100) : 0;

    if (job.status === "completed" || job.status === "aborted") {
      return (
        <span className="text-sm">
          <span className="text-success font-medium">{successful_count}</span>
          {failed_count > 0 && (
            <>
              {" / "}
              <span className="text-destructive">{failed_count} failed</span>
            </>
          )}
          <span className="text-muted-foreground"> of {total_count}</span>
        </span>
      );
    }

    return (
      <span className="text-sm">
        {processed_count} / {total_count}{" "}
        <span className="text-muted-foreground">({percentage}%)</span>
      </span>
    );
  };

  const getActionButtons = (job: WelcomeEmailJob) => {
    const isLoading = actionInProgress === job.id;
    const stuck = isStuckJob(job);
    
    return (
      <div className="flex gap-2">
        {/* Abort button for processing jobs */}
        {job.status === "processing" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                size="sm" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <StopCircle className="h-3 w-3 mr-1" />
                )}
                Abort
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Abort Email Job?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will stop sending remaining emails. {job.processed_count} of {job.total_count} emails have already been sent.
                  <br /><br />
                  You can resume this job later if needed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => handleAbort(job.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Abort Job
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        
        {/* Resume button for aborted or stuck jobs */}
        {(job.status === "aborted" || stuck) && job.processed_count < job.total_count && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleResume(job.id, job.processed_count, job.total_count)}
            disabled={isLoading}
            className="border-success text-success hover:bg-success/10"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Play className="h-3 w-3 mr-1" />
            )}
            Resume
          </Button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Total Jobs</p>
              </div>
              <p className="text-2xl font-bold">{stats.totalJobs}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Emails Sent</p>
              </div>
              <p className="text-2xl font-bold">{stats.totalEmailsSent}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Success Rate</p>
              </div>
              <p className="text-2xl font-bold">{stats.successRate}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">In Progress</p>
              </div>
              <p className="text-2xl font-bold">{stats.inProgress}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Jobs Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Bulk Welcome Email Jobs</CardTitle>
            <CardDescription>History of all bulk welcome email operations</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {jobs && jobs.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Initiated By</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => (
                    <Collapsible key={job.id} asChild open={expandedJobId === job.id}>
                      <>
                        <TableRow className="cursor-pointer hover:bg-muted/50">
                          <TableCell>
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                              >
                                {expandedJobId === job.id ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </CollapsibleTrigger>
                          </TableCell>
                          <TableCell>{getStatusBadge(job)}</TableCell>
                          <TableCell>{getProgressDisplay(job)}</TableCell>
                          <TableCell className="max-w-xs truncate" title={job.email_subject}>
                            {job.email_subject}
                          </TableCell>
                          <TableCell>{job.admin_name}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(job.created_at), "MMM d, yyyy h:mm a")}
                          </TableCell>
                          <TableCell>
                            {getActionButtons(job)}
                          </TableCell>
                        </TableRow>
                        <CollapsibleContent asChild>
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={7} className="p-4">
                              <div className="space-y-3">
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground">Email Subject</p>
                                  <p className="text-sm">{job.email_subject}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-muted-foreground">Email Body Preview</p>
                                  <p className="text-sm whitespace-pre-wrap bg-background rounded p-2 border max-h-40 overflow-y-auto">
                                    {job.email_body?.length > 500
                                      ? job.email_body.slice(0, 500) + "..."
                                      : job.email_body || "No body content"}
                                  </p>
                                </div>
                                {job.completed_at && (
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground">
                                      {job.status === 'aborted' ? 'Aborted At' : 'Completed At'}
                                    </p>
                                    <p className="text-sm">
                                      {format(new Date(job.completed_at), "MMM d, yyyy h:mm a")}
                                    </p>
                                  </div>
                                )}
                                {job.error_details && Array.isArray(job.error_details) && job.error_details.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-destructive mb-2">Failed Emails ({job.error_details.length})</p>
                                    <div className="bg-destructive/10 rounded border border-destructive/20 overflow-hidden">
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="border-destructive/20">
                                            <TableHead className="text-destructive text-xs">Email</TableHead>
                                            <TableHead className="text-destructive text-xs">Error</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {(job.error_details as Array<{ email: string; error?: string }>).map((failure, idx) => (
                                            <TableRow key={idx} className="border-destructive/20">
                                              <TableCell className="text-xs py-2">{failure.email}</TableCell>
                                              <TableCell className="text-xs py-2 text-destructive">
                                                {failure.error || "Unknown error"}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </div>
                                )}
                                <div className="flex gap-6 text-sm text-muted-foreground">
                                  <span>Total: {job.total_count}</span>
                                  <span>Processed: {job.processed_count}</span>
                                  <span className="text-success">Successful: {job.successful_count}</span>
                                  <span className="text-destructive">Failed: {job.failed_count}</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No bulk email jobs yet</p>
              <p className="text-sm">Jobs will appear here when you send bulk welcome emails</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
